import { api } from '@/api';
import {
  createBooking,
  fetchBooking,
  fetchIssuer,
} from '@/features/purchase/purchaseService';
import { createAsyncThunk } from '@/store/utils';
import type { ApiErrorResponse, ErrorResponse } from '@/types/api';
import type {
  AddAncillariesBulkResponseDTO,
  AdditionalOfferPartDTO,
  BookingDTO,
  CreateBookingResponseDTO,
  FulfillmentDTO,
  NonTripAdmissionOfferPartDTO,
  PayWithAdyenBodyDTO,
  PayWithAdyenResponseDTO,
  PayWithExternalPaymentRequestBodyDTO,
  RemoveAncillaryParametersDTO,
  SearchJourneyOfferDTO,
  SearchJourneyOffersDTO,
  SearchNonTripOfferDTO,
  SearchNonTripOffersDTO,
  SearchOffersDTO,
} from '@/types/dto/agent';
import type {
  AdditionalOfferItem,
  AdditionalOffersCollection,
  OfferMapByLegId,
  OfferMapItem,
} from '@/types/offer';
import type {
  NonTripOfferSearchRequestBodyWithSearchFormPassengers,
  OfferSearchRequestBodyWithSearchFormPassengers,
} from '@/types/purchase';
import type { TravelAccountDTO, TravelAccountsMap } from '@/types/travelpass';
import type { AncillaryValues, CheckoutValues } from '@/utils/zodSchema';
import { createAction } from '@reduxjs/toolkit';
import type { SearchFormValues } from '@turnit-ride-ui/webshop-search-widget';
import _partition from 'lodash/partition';

export const getOutboundJourneys = createAsyncThunk<
  SearchOffersDTO | null,
  Omit<
    OfferSearchRequestBodyWithSearchFormPassengers,
    'currency' | 'promotionCodes' | 'corporateCodes'
  >
>(
  'purchase/getOutboundJourneys',
  async (payload, { dispatch }) => await dispatch(getJourneys(payload)).unwrap()
);

export const getInboundJourneys = createAsyncThunk<
  SearchOffersDTO | null,
  Omit<
    OfferSearchRequestBodyWithSearchFormPassengers,
    'currency' | 'promotionCodes' | 'corporateCodes'
  >
>(
  'purchase/getInboundJourneys',
  async (payload, { dispatch }) => await dispatch(getJourneys(payload)).unwrap()
);

export const getJourneys = createAsyncThunk<
  SearchOffersDTO | null,
  Omit<
    OfferSearchRequestBodyWithSearchFormPassengers,
    'currency' | 'promotionCodes' | 'corporateCodes'
  >
>(
  'purchase/getJourneys',
  async ({ passengers, promoCodes, ...rest }, { getState }) => {
    const {
      configuration: { salesPointConfiguration },
    } = getState();

    if (!salesPointConfiguration?.currencies?.length) {
      return null;
    }

    try {
      return (
        await api.agentApi.Offers_FindOffers(null, {
          ...rest,
          passengerSpecifications: passengers.map(
            ({ age, externalReference, type }) => ({
              age,
              externalReference,
              type,
            })
          ),
          currency: salesPointConfiguration.currencies[0],
          promotionCodes: (promoCodes ?? []).map(({ code }) => ({
            code,
          })),
          corporateCodes: [],
        })
      ).data;
    } catch {
      return {
        journeys: [],
      };
    }
  }
);

export const getNonTripOffers = createAsyncThunk<
  SearchNonTripOffersDTO | null,
  Omit<
    NonTripOfferSearchRequestBodyWithSearchFormPassengers,
    'currency' | 'promotionCodes' | 'corporateCodes'
  >
>(
  'purchase/getNonTripOffers',
  async ({ passengers, promoCodes, ...rest }, { getState }) => {
    const {
      configuration: { salesPointConfiguration },
    } = getState();

    if (!salesPointConfiguration?.currencies?.length) {
      return null;
    }

    try {
      return (
        await api.agentApi.Offers_FindNonTripOffers(null, {
          ...rest,
          passengerSpecifications: passengers.map(
            ({ age, externalReference, type }) => ({
              age,
              externalReference,
              type,
            })
          ),
          currency: salesPointConfiguration.currencies[0],
          promotionCodes: (promoCodes ?? []).map(({ code }) => ({
            code,
          })),
          corporateCodes: [],
        })
      ).data;
    } catch {
      return { nonTripOffers: [] };
    }
  }
);

export const updateSelectedNonTripOffer = createAction<SearchNonTripOfferDTO>(
  'purchase/updateSelectedNonTripOffer'
);

export const setSelectedOutboundJourney = createAction<SearchJourneyOffersDTO>(
  'purchase/setSelectedOutboundJourney'
);

export const setSelectedInboundJourney = createAction<SearchJourneyOffersDTO>(
  'purchase/setSelectedInboundJourney'
);

export const setSelectedOutboundOfferMapByLegId = createAction<OfferMapByLegId>(
  'purchase/setSelectedOutboundOfferMapByLegId'
);

export const updateSelectedOutboundOfferMapByLegId = createAction<OfferMapItem>(
  'purchase/updateSelectedOutboundOfferMapLegId'
);

export const setSelectedInboundOfferMapByLegId = createAction<OfferMapByLegId>(
  'purchase/setSelectedInboundOfferMapByLegId'
);

export const updateSelectedInboundOfferMapByLegId = createAction<OfferMapItem>(
  'purchase/updateSelectedInboundOfferMapLegId'
);

export const resetPurchase = createAction('purchase/resetPurchase');

export const getPurchaseFlowBookingAdditionalOffersSearch = createAsyncThunk<
  {
    outboundAdditionalOffers: Array<AdditionalOffersCollection>;
    inboundAdditionalOffers: Array<AdditionalOffersCollection>;
  },
  string
>(
  'purchase/getPurchaseFlowBookingAdditionalOffersSearch',
  async (bookingId, { getState }) => {
    const booking = getState().purchase.booking;

    const [outboundBookedTrips, inboundBookedTrips] = _partition(
      booking?.bookedTrips,
      'isOutbound'
    );

    const outboundOfferIds =
      outboundBookedTrips
        .flatMap((bookedTrip) =>
          bookedTrip.bookedOffers?.map((bookedOffer) => bookedOffer.id)
        )
        .filter((id): id is string => Boolean(id)) || [];

    const inboundOfferIds =
      inboundBookedTrips
        .flatMap((bookedTrip) =>
          bookedTrip.bookedOffers?.map((bookedOffer) => bookedOffer.id)
        )
        .filter((id): id is string => Boolean(id)) || [];

    const responseItems = (
      await api.agentApi.BookedOffers_GetAdditionalOffersSearch(
        {
          bookingId,
        },
        {
          bookedOfferIds: [...outboundOfferIds, ...inboundOfferIds],
        }
      )
    ).data.items;

    const mergeOfferParts = ({
      existingParts,
      newParts,
      bookedOfferId,
      additionalOfferId,
    }: {
      existingParts?: Array<AdditionalOfferItem>;
      newParts?: Array<AdditionalOfferPartDTO>;
      bookedOfferId: string;
      additionalOfferId?: string;
    }): Array<AdditionalOfferItem> =>
      additionalOfferId
        ? [
            ...(existingParts || []),
            ...(newParts || []).map((offer) => ({
              ...offer,
              bookedOfferId,
              additionalOfferId,
            })),
          ]
        : [];

    const additionalOfferCollectionByOfferId =
      responseItems?.reduce<Record<string, AdditionalOffersCollection>>(
        (
          acc,
          {
            bookedOfferId,
            additionalOfferId,
            ancillaryOfferParts,
            reservationOfferParts,
            admissionOfferParts,
          }
        ) => {
          return !bookedOfferId
            ? acc
            : {
                ...acc,
                [bookedOfferId]: {
                  ancillaryOfferParts: mergeOfferParts({
                    existingParts: acc[bookedOfferId]?.ancillaryOfferParts,
                    newParts: ancillaryOfferParts,
                    bookedOfferId,
                    additionalOfferId,
                  }),
                  reservationOfferParts: mergeOfferParts({
                    existingParts: acc[bookedOfferId]?.reservationOfferParts,
                    newParts: reservationOfferParts,
                    bookedOfferId,
                    additionalOfferId,
                  }),
                  admissionOfferParts: mergeOfferParts({
                    existingParts: acc[bookedOfferId]?.admissionOfferParts,
                    newParts: admissionOfferParts,
                    bookedOfferId,
                    additionalOfferId,
                  }),
                },
              };
        },
        {}
      ) || {};

    return {
      outboundAdditionalOffers: outboundOfferIds
        .map((id) => additionalOfferCollectionByOfferId[id])
        .filter(Boolean),
      inboundAdditionalOffers: inboundOfferIds
        .map((id) => additionalOfferCollectionByOfferId[id])
        .filter(Boolean),
    };
  }
);

export const updatePurchaseFlowBookingPassengers = createAsyncThunk<
  object,
  CheckoutValues['passengers']
>(
  'purchase/updatePurchaseFlowBookingPassengers',
  async (passengers, { getState }) => {
    const bookingId = getState().purchase.booking?.id;

    if (!bookingId) {
      return;
    }

    const responses = [];

    for (const { id: passengerId, ...rest } of passengers) {
      const cleanedPayload = Object.fromEntries(
        Object.entries(rest).filter(([_, value]) => value !== '')
      );

      const response = await api.agentApi.Bookings_UpdatePassenger(
        { bookingId, passengerId },
        cleanedPayload
      );

      responses.push(response);
    }

    return responses;
  }
);

export const updatePurchaseFlowBookingPurchaser = createAsyncThunk<
  object,
  CheckoutValues['purchaser']
>(
  'purchase/updatePurchaseFlowBookingPurchaser',
  async (purchaser, { getState }) => {
    const bookingId = getState().purchase.booking?.id;

    if (!bookingId) {
      return;
    }

    return await api.agentApi.Bookings_UpdatePurchaser(
      { bookingId },
      purchaser
    );
  }
);

export const addPurchaseFlowBookingAncillaries = createAsyncThunk<
  AddAncillariesBulkResponseDTO | ErrorResponse,
  AncillaryValues['selectedAncillaries']
>(
  'purchase/addPurchaseFlowBookingAncillaries',
  async (values, { getState }) => {
    const bookingId = getState().purchase.booking?.id;

    if (!bookingId) {
      return;
    }

    const bookedOfferId = values[0].bookedOfferId;

    return await api.agentApi
      .BookedOffers_AddAncillariesBulk(
        { bookingId, bookedOfferId },
        {
          ancillaries: values.map((selectedAncillary) => ({
            ancillaryId: selectedAncillary.id,
            offerId: selectedAncillary.additionalOfferId,
            passengerRefs: selectedAncillary.passengersExternalReferences,
          })),
        }
      )
      .catch((res: ApiErrorResponse) => res.response!.data);
  }
);

export const removePurchaseFlowBookingAncillary = createAsyncThunk<
  object,
  Omit<RemoveAncillaryParametersDTO, 'bookingId'>
>(
  'purchase/removePurchaseFlowBookingAncillary',
  async (pathParams, { getState }) => {
    const bookingId = getState().purchase.booking?.id;

    if (!bookingId) {
      return;
    }

    return (
      await api.agentApi.BookedOffers_DeleteAncillary({
        bookingId,
        ...pathParams,
      })
    ).data;
  }
);

const preparePromoCodes = (
  offers:
    | Array<SearchJourneyOfferDTO>
    | Array<NonTripAdmissionOfferPartDTO> = []
) =>
  Object.entries(
    offers.reduce<Record<string, string | undefined>>((acc, offer) => {
      (offer.appliedReductions ?? []).forEach(({ code, issuer }) => {
        if (code && !acc[code]) {
          acc[code] = issuer;
        }
      });

      return acc;
    }, {})
  ).map(([code, issuer]) => ({ code, issuer }));

export const createPurchaseFlowTripOfferBooking = createAsyncThunk<
  CreateBookingResponseDTO,
  SearchFormValues['passengers']
>(
  'purchase/createPurchaseFlowTripOfferBooking',
  async (passengers, { getState }) => {
    const { outbound, inbound } = getState().purchase;
    const passengerExternalReferences = passengers.map(
      ({ externalReference }) => externalReference
    );

    const selectedOffers = [
      ...Object.values(outbound.selectedOfferMapByLegId),
      ...Object.values(inbound.selectedOfferMapByLegId),
    ].filter(
      (
        offer
      ): offer is Omit<SearchJourneyOfferDTO, 'id'> & {
        id: NonNullable<SearchJourneyOfferDTO['id']>;
      } => !!offer.id
    );
    const offers = selectedOffers.map(({ id }) => ({
      id,
      passengerExternalReferences,
    }));
    const promotionCodes = preparePromoCodes(selectedOffers);

    return createBooking(offers, passengers, promotionCodes);
  }
);

export const createPurchaseFlowNonTripOfferBooking = createAsyncThunk<
  CreateBookingResponseDTO,
  SearchFormValues['passengers']
>(
  'purchase/createPurchaseFlowNonTripOfferBooking',
  async (passengers, { getState }) => {
    const { nonTripOffers } = getState().purchase;
    const passengerExternalReferences = passengers.map(
      ({ externalReference }) => externalReference
    );
    const offers = nonTripOffers.selectedNonTripOffer?.id
      ? [
          {
            id: nonTripOffers.selectedNonTripOffer?.id,
            passengerExternalReferences,
          },
        ]
      : [];

    const promotionCodes = preparePromoCodes(
      nonTripOffers.selectedNonTripOffer?.admissionOfferParts
    );

    return createBooking(offers, passengers, promotionCodes);
  }
);

export const getPurchaseFlowBookingById = createAsyncThunk<
  BookingDTO,
  string | null | undefined
>('purchase/getPurchaseFlowBookingById', fetchBooking);

// Separate action to fetch booking while on seat selection modal to avoid duplicate loading spinners
export const getSeatSelectionBookingById = createAsyncThunk<
  BookingDTO,
  string | null | undefined
>('purchase/getSeatSelectionBookingById', fetchBooking);

export const deletePurchaseFlowBooking = createAsyncThunk<
  object,
  NonNullable<BookingDTO['id']>
>('purchase/deletePurchaseFlowBooking', async (bookingId) => {
  return await api.agentApi.Bookings_DeleteBooking({ bookingId });
});

export const getPurchaseFlowTravelAccountsMap = createAsyncThunk<
  TravelAccountsMap,
  Array<FulfillmentDTO>
>('purchase/getPurchaseFlowTravelAccountsMap', async (fulfillments) => {
  const isValidFulfillment = (
    fulfillment: FulfillmentDTO
  ): fulfillment is { controlNumber: string; issuer: string } =>
    Boolean(fulfillment.controlNumber) && Boolean(fulfillment.issuer);

  const travelAccounts = await Promise.all(
    fulfillments.filter(isValidFulfillment).map((fulfillment) =>
      fetchIssuer({
        controlNumber: fulfillment.controlNumber,
        issuer: fulfillment.issuer,
      })
    )
  );

  return travelAccounts.reduce<
    Record<
      `${NonNullable<TravelAccountDTO>['issuer']}-${NonNullable<TravelAccountDTO['number']>}`,
      TravelAccountDTO
    >
  >((acc, { travelAccount }) => {
    if (travelAccount?.issuer && travelAccount.number) {
      acc[`${travelAccount.issuer}-${travelAccount.number}`] = travelAccount;
    }

    return acc;
  }, {});
});

export const initiateAdyenPayment = createAsyncThunk<
  PayWithAdyenResponseDTO,
  { bookingId: string } & PayWithAdyenBodyDTO
>('purchase/initiateAdyenPayment', async ({ bookingId, ...body }) => {
  return (await api.agentApi.Payments_PayWithAdyenCheckout({ bookingId }, body))
    .data;
});

export const initiateExternalPayment = createAsyncThunk<
  unknown,
  { bookingId: string } & PayWithExternalPaymentRequestBodyDTO
>('purchase/initiateExternalPayment', async ({ bookingId, ...body }) => {
  return await api.agentApi.Payments_PayWithExternalPayment(
    { bookingId },
    body
  );
});

export const initiateZeroPayment = createAsyncThunk<unknown, string>(
  'purchase/initiateZeroPayment',
  async (bookingId) => {
    return (
      await api.agentApi.Payments_PayWithZeroPayment(
        { bookingId },
        { offerPartIds: [] }
      )
    ).data;
  }
);
